import { supabase } from '@/config/supabase';
import { CHALLENGE_TEMPLATES, MAX_ACTIVE_CHALLENGES, type ChallengeData, type ChallengeObjective } from '@/models/challenges';

export class ChallengeManager {
  private userId: string = '';
  private activeChallenges: ChallengeData[] = [];

  setUserId(id: string): void { this.userId = id; }

  async loadChallenges(): Promise<ChallengeData[]> {
    if (!this.userId) return [];
    const { data, error } = await supabase
      .from('challenges')
      .select('*')
      .or(`challenger_id.eq.${this.userId},challenged_id.eq.${this.userId}`)
      .in('status', ['pending', 'active'])
      .order('created_at', { ascending: false })
      .limit(10);

    if (error || !data) return [];
    this.activeChallenges = data.map(this.mapRow);
    return this.activeChallenges;
  }

  async createChallenge(challengedId: string, objective: ChallengeObjective): Promise<boolean> {
    if (this.userId === challengedId) return false;

    // Check max active
    const activeCount = this.activeChallenges.filter(
      c => c.challengerId === this.userId && (c.status === 'pending' || c.status === 'active')
    ).length;
    if (activeCount >= MAX_ACTIVE_CHALLENGES) return false;

    const template = CHALLENGE_TEMPLATES.find(t => t.objective === objective);
    if (!template) return false;

    const { error } = await supabase.from('challenges').insert({
      challenger_id: this.userId,
      challenged_id: challengedId,
      objective,
      target_value: template.targetValue,
      gems_reward: template.gemsReward,
    });
    return !error;
  }

  async acceptChallenge(challengeId: string): Promise<boolean> {
    const { error } = await supabase
      .from('challenges')
      .update({ status: 'active' })
      .eq('id', challengeId)
      .eq('challenged_id', this.userId)
      .eq('status', 'pending');
    return !error;
  }

  async updateProgress(challengeId: string, progress: number): Promise<ChallengeData | null> {
    const challenge = this.activeChallenges.find(c => c.id === challengeId);
    if (!challenge || challenge.status !== 'active') return null;

    const isChallenger = challenge.challengerId === this.userId;
    const field = isChallenger ? 'challenger_progress' : 'challenged_progress';

    const { data, error } = await supabase
      .from('challenges')
      .update({ [field]: progress })
      .eq('id', challengeId)
      .select()
      .single();

    if (error || !data) return null;

    const updated = this.mapRow(data);

    // Check completion
    if (updated.challengerProgress >= updated.targetValue || updated.challengedProgress >= updated.targetValue) {
      const winnerId = updated.challengerProgress >= updated.targetValue ? updated.challengerId : updated.challengedId;
      await supabase
        .from('challenges')
        .update({ status: 'completed', winner_id: winnerId })
        .eq('id', challengeId);
      updated.status = 'completed';
      updated.winnerId = winnerId;
    }

    return updated;
  }

  getActiveChallenges(): ChallengeData[] {
    return this.activeChallenges;
  }

  private mapRow(row: any): ChallengeData {
    return {
      id: row.id,
      challengerId: row.challenger_id,
      challengedId: row.challenged_id,
      challengerName: row.challenger_name ?? 'Unknown',
      challengedName: row.challenged_name ?? 'Unknown',
      objective: row.objective,
      targetValue: row.target_value,
      challengerProgress: row.challenger_progress,
      challengedProgress: row.challenged_progress,
      status: row.status,
      winnerId: row.winner_id,
      gemsReward: row.gems_reward,
      createdAt: row.created_at,
      expiresAt: row.expires_at,
    };
  }
}
